import {
    IOrder,
    IOrderCreate,
    IOrderDataUpdate,
    IOrderForm, IOrderPriceUpdate,
    IOrderUpdate,
} from '../typing/order.interface';
import { OrderStatusEnum } from '../enums/status.enum';
import { TimeZoneService } from '../services/time-zone.service';

export class Order {
    data: IOrder;
    private timeZoneService: TimeZoneService;

    constructor(data: IOrder, timeZoneService: TimeZoneService) {
        this.timeZoneService = timeZoneService;
        if (data.location?.address && data.addressDetails) {
            data.location.address = data.addressDetails;
        }

        this.data = {
            ...data,
            dateFrom: this.adjustDateFromServer(data.dateFrom),
            dateTo: this.adjustDateFromServer(data.dateTo),
        };
    }

    // Преобразование дат при получении с сервера
    private adjustDateFromServer(date: Date | string): Date {
        return this.timeZoneService.setCurrentTimeZoneDate(date);
    }

    dataForCreate(formData: IOrderForm): IOrderCreate{
        return {
            dateFrom: formData.dateFrom ? new Date(formData.dateFrom) : new Date(),
            dateTo: formData.dateTo ? new Date(formData.dateTo) : new Date(),
            contactId: formData.contactId,
            groupId: formData.groupId,
            projectId: formData.projectId,
            status: formData.status,
            locationId: formData.locationId,
            addressDetails: formData.addressDetails || undefined,
        };
    }

    dataForUpdate(formData: IOrderForm): IOrderUpdate {
        return {
            id: this.data.id,
            additionalInfo: formData.additionalInfo,
            addressDetails: formData.addressDetails || undefined,
            agreement: formData.agreement,
            conditionsCheck: formData.conditionsCheck,
            contactId: this.data.contact.id,
            dateFrom: formData.dateFrom ? new Date(formData.dateFrom) : undefined,
            dateTo: formData.dateTo ? new Date(formData.dateTo) : undefined,
            description: formData.description,
            executorId: formData.executorId,
            groupId: formData.groupId,
            projectId: formData.projectId,
            signature: formData.signature,
            orderPrices: formData.orderPrices?.map(price => ({
                priceId: price.priceId,
                itemId: price.itemId,
                quantity: price.quantity,
                customPrice: price.customPrice ?? null,
            })) || undefined,
            status: formData.status,
            discount: formData.discount,
            rejectDescription: formData.rejectDescription,
            rejectReason: formData.rejectReason,
            locationId: formData.locationId,
            prepayment: formData.prepayment,
            amountPaid: formData.amountPaid,
            customFieldsParams: formData.customFieldsParams,
        };
    }

    formData(): Partial<IOrderForm> {
        return {
            additionalInfo: this.data.additionalInfo || '',
            addressDetails: this.data.addressDetails || '',
            agreement: this.data.agreement || '',
            conditionsCheck: this.data.conditionsCheck || false,
            contactId: this.data.contact?.id || null,
            dateFrom: this.data.dateFrom instanceof Date ? this.data.dateFrom.toISOString() : null,
            dateTo: this.data.dateTo instanceof Date ? this.data.dateTo.toISOString() : null,
            description: this.data.description || '',
            executorId: this.data.executor?.id || null,
            groupId: this.data.group?.id || null,
            projectId: this.data.project?.id || null,
            signature: this.data.signature || '',
            orderPrices: this.data.orderPrices?.map(price => ({
                priceId: price.price.id || 0,
                itemId: price.item?.id || 0,
                quantity: price.quantity || 1,
                customPrice: price.customPrice ?? null,
            })) || [],
            prepayment: this.data.prepayment || 0,
            amountPaid: this.data.amountPaid || 0,
            status: this.data.status || OrderStatusEnum.active,
            discount: !!this.data.discount,
            rejectDescription: this.data.rejectDescription || '',
            rejectReason: this.data.rejectReason,
            locationId: this.data.location.id,
            customFieldsParams: this.data.customFieldsParams,
        };
    }

    multiUpdate(formData: IOrderDataUpdate, isOld?: Order | undefined): IOrderDataUpdate {
        const updatedData: IOrderDataUpdate = {
            id: this.data.id,
            dateFrom: isOld ? this.data.dateFrom : formData.dateFrom || undefined,
            status: isOld ? this.data.status : formData.status || this.updateStatus(this.data.status),
            dateTo: isOld ? this.data.dateTo : formData.dateTo || undefined,
            groupId: isOld ? this.data.group?.id : formData.groupId,
            executorId: isOld ?
                this.data.executor?.id : formData.status === OrderStatusEnum.active ? null : (formData.executorId || undefined),
            queueNumber: this.data.queueNumber || Infinity,
        };

        // Удаляем поля, если они пустые
        Object.keys(updatedData).forEach(key => {
            if (updatedData[key as keyof IOrderDataUpdate] === undefined) {
                delete updatedData[key as keyof IOrderDataUpdate];
            }
        });

        return updatedData;
    }

    updateStatus(status: OrderStatusEnum): OrderStatusEnum {
        if (status === OrderStatusEnum.active) {
            return OrderStatusEnum.destributed;
        }
        return status;
    }

    //когда мы автоматически распределяем заявки или перетягиваем на работника
    dataForMultipleUpdateExecutorIdAndStatus(executorId?: number): IOrderDataUpdate {
        return {
            id: this.data.id,
            status: this.data.status !== OrderStatusEnum.active ? this.data.status : OrderStatusEnum.destributed,
            executorId: executorId || this.data.executor?.id,
            queueNumber: this.data.queueNumber || undefined,
        };
    }

    dataForCancelDistributedOrders(): IOrderDataUpdate {
        return {
            id: this.data.id,
            status: OrderStatusEnum.active,
            executorId: null
        };
    }

    dataForStatusTo(status: OrderStatusEnum): IOrderDataUpdate {
        return {
            id: this.data.id,
            status: status
        };
    }

    refreshQueue(order: Order, idx: number): IOrderDataUpdate {
        return {
            id: order.data.id,
            queueNumber: idx + 1
        };
    }

    orderPrice(): IOrderPriceUpdate[] {
        return this.data.orderPrices.map((item) => {
            return {
                priceId: item.price.id,
                itemId: item.item?.id,
                quantity: item.quantity || 1,
                customPrice: item.customPrice,
                // Если в заявке задана своя цена, используем ее; иначе берем обычную цену из прайса.
                priceValue: item.customPrice ?? item.price.price ?? 0,
                // Для режима скидки: приоритет у кастомной цены, иначе берем скидочную цену из прайса.
                discountPriceValue: item.customPrice ?? item.price.discountPrice ?? 0,
            };
        });
    }

    // метод подсчета стоимости заявки
    totalCost(prices: IOrderPriceUpdate[], discount: boolean): number {
        if (!prices || !prices.every(el => el && el.priceId)) {
            return 0;
        }

        let totalCostValue = 0;
        prices.forEach((orderPrice) => {
            if (!orderPrice) { return; }

            // Приоритет цены: customPrice (переопределение в заявке) -> priceValue (из формы/прайса) -> 0.
            const priceValue = orderPrice.customPrice ?? orderPrice.priceValue ?? 0;
            // Для скидки логика та же: если есть customPrice, он главнее скидочной цены из прайса.
            const discountPriceValue = orderPrice.customPrice ?? orderPrice.discountPriceValue ?? priceValue;

            if (!discount) {
                totalCostValue += priceValue * orderPrice.quantity;
            } else {
                totalCostValue += discountPriceValue * orderPrice.quantity;
            }
        });

        return Number(totalCostValue.toFixed(2));
    }
}
